import { useMutation } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * Upcoming-appointment count for a (provider, location, practice, org)
 * tuple — used as the disable-location guard when editing a practitioner's
 * working hours (F-PRS-117).
 *
 *   GET /api/Appointment/GetUpcomingAppointmentCount
 *       ?providerId=&LocationId=&PracticeId=&organizationId=
 *
 * Flutter parity: `settings_data_source_impl.dart:1406-1422`
 * (`featureAppointmentCount`), dispatched from `doctor_schedule_tab.dart:469`
 * when a location is disabled (archived) from a working-hours profile. Note
 * the mixed casing in the query keys — `LocationId` / `PracticeId` are
 * PascalCase, `providerId` / `organizationId` camelCase — copied verbatim
 * from the Flutter datasource (the cURL-validated source for this module).
 *
 * Returns the count as a number. The endpoint replies with a bare count
 * (`utf8.decode(response.data)` in Flutter); `apiClient.get` JSON-parses it,
 * so it may arrive as a number or a numeric string — coerced here.
 *
 * Modelled as a mutation (not a query) because it's fired imperatively at
 * save-time for a specific tuple, not bound to render state.
 */
export interface UpcomingAppointmentCountParams {
  providerId: string | null | undefined;
  locationId: string | null | undefined;
  practiceId: string | null | undefined;
  organizationId: string | null | undefined;
}

export function useUpcomingAppointmentCount() {
  return useMutation<number, Error, UpcomingAppointmentCountParams>({
    mutationFn: async (p) => {
      // URLSearchParams preserves key casing EXACTLY — it does not lowercase
      // params (Samer #492 §3). The mixed casing (LocationId/PracticeId
      // PascalCase, providerId/organizationId camelCase) is load-bearing and
      // copied verbatim from the Flutter datasource. The query is baked into
      // the URL string passed to apiClient.get, so no downstream serializer
      // can re-case it.
      const qs = new URLSearchParams({
        providerId: p.providerId ?? '',
        LocationId: p.locationId ?? '',
        PracticeId: p.practiceId ?? '',
        organizationId: p.organizationId ?? '',
      });
      const raw = await get<number | string>(
        `/api/Appointment/GetUpcomingAppointmentCount?${qs.toString()}`,
      );
      const n = typeof raw === 'number' ? raw : Number(raw);
      return Number.isFinite(n) ? n : 0;
    },
  });
}
